import React, { useEffect, useMemo, useState } from "react";
import {
  getContactDetail,
  updateContact,
  deleteContact,
  addContactListing,
  removeContactListing,
  getListings
} from "../api/client.js";
import { formatDateTime } from "./formatters.js";

const CONTACT_TYPE_OPTIONS = [
  { label: "Buyer", value: "BUYER" },
  { label: "Seller", value: "SELLER" },
  { label: "Buyer + Seller", value: "BUYER_SELLER" },
  { label: "Legal", value: "LEGAL" },
  { label: "Agent", value: "AGENT" },
  { label: "Developer", value: "DEVELOPER" },
  { label: "Company", value: "COMPANY" },
  { label: "Interest Registered", value: "INTEREST_REGISTERED" }
];

const SOURCE_LABELS = {
  USER_ADDED: "User added",
  ADMIN_EDIT: "Admin edit",
  IMPORTED: "Imported"
};

export default function CrmContactDrawer({ open, contactId, onClose, onSaved, onDeleted }) {
  const [contact, setContact] = useState(null);
  const [formState, setFormState] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");
  const [listingQuery, setListingQuery] = useState("");
  const [listingResults, setListingResults] = useState([]);
  const [listingLoading, setListingLoading] = useState(false);
  const [listingError, setListingError] = useState("");

  useEffect(() => {
    if (!open || !contactId) {
      setContact(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError("");
    getContactDetail(contactId)
      .then((data) => {
        if (cancelled) return;
        setContact(data);
        setFormState({
          name: data.name || "",
          email: data.emails?.[0] || "",
          phone: data.phones?.[0] || "",
          company: data.company || "",
          role: data.role || "",
          type: data.type,
          tags: (data.tags || []).join(", "),
          notes: data.notes || "",
          marketingOptIn: Boolean(data.marketingOptIn),
          marketingContactAllowed: Boolean(data.marketingContactAllowed)
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || "Unable to load contact");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, contactId]);

  useEffect(() => {
    if (!listingQuery.trim()) {
      setListingResults([]);
      return;
    }
    let cancelled = false;
    setListingLoading(true);
    getListings({ q: listingQuery, page: 1, pageSize: 5 })
      .then((data) => {
        if (!cancelled) {
          setListingResults(data.items || []);
          setListingError("");
        }
      })
      .catch((err) => {
        if (!cancelled) setListingError(err.message || "Unable to search listings");
      })
      .finally(() => {
        if (!cancelled) setListingLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [listingQuery]);

  const associatedListings = useMemo(() => {
    if (!contact?.contactListings) return [];
    return contact.contactListings.map((link) => link.listing);
  }, [contact]);

  const handleFieldChange = (field) => (event) => {
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!contact) return;
    setSaving(true);
    setError("");
    try {
      const payload = {
        name: formState.name,
        emails: formState.email ? [formState.email.trim()] : [],
        phones: formState.phone ? [formState.phone.trim()] : [],
        tags: formState.tags
          ? formState.tags
              .split(",")
              .map((value) => value.trim())
              .filter(Boolean)
          : [],
        type: formState.type,
        notes: formState.notes,
        marketingOptIn: formState.marketingOptIn,
        marketingContactAllowed: formState.marketingContactAllowed,
        company: formState.company,
        role: formState.role
      };
      const updated = await updateContact(contact.id, payload);
      setContact(updated);
      onSaved?.(updated);
    } catch (err) {
      setError(err.message || "Unable to save contact");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!contact) return;
    if (!window.confirm("Delete this contact? This operation can be restored by admins within 30 days.")) {
      return;
    }
    setDeleting(true);
    try {
      await deleteContact(contact.id);
      onDeleted?.(contact.id);
      onClose();
    } catch (err) {
      setError(err.message || "Unable to delete contact");
    } finally {
      setDeleting(false);
    }
  };

  const handleAddListing = async (listing) => {
    if (!contact) return;
    try {
      const result = await addContactListing(contact.id, listing.id);
      setContact((prev) => ({
        ...prev,
        contactListings: [...(prev.contactListings || []), { listing, id: result.id }]
      }));
      setListingQuery("");
      setListingResults([]);
    } catch (err) {
      setError(err.message || "Unable to link listing");
    }
  };

  const handleRemoveListing = async (listing) => {
    if (!contact) return;
    try {
      await removeContactListing(contact.id, listing.id);
      setContact((prev) => ({
        ...prev,
        contactListings: (prev.contactListings || []).filter((link) => link.listingId !== listing.id)
      }));
    } catch (err) {
      setError(err.message || "Unable to unlink listing");
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[110] flex">
      <div className="flex-1" onClick={onClose} aria-hidden />
      <div className="w-[420px] max-w-full overflow-y-auto bg-slate-950/95 border-l border-white/10 p-6 shadow-2xl">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.4em] text-slate-500">Contact detail</p>
            <h2 className="text-2xl font-semibold text-white">{contact?.name || "Loading..."}</h2>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white">
            Close
          </button>
        </div>
        {loading && (
          <div className="mt-6 text-sm text-slate-400">Loading contact…</div>
        )}
        {error && (
          <div className="mt-4 rounded-2xl border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-sm text-rose-200">
            {error}
          </div>
        )}
        {!loading && contact && (
          <div className="mt-6 space-y-4">
            <div className="space-y-2">
              <label className="text-xs uppercase tracking-[0.3em] text-slate-400">
                Full name
                <input
                  value={formState.name}
                  onChange={handleFieldChange("name")}
                  className="mt-1 w-full rounded-2xl border border-white/10 bg-black/40 px-3 py-2 text-sm text-white focus:border-emerald-400 focus:outline-none"
                />
              </label>
              <label className="text-xs uppercase tracking-[0.3em] text-slate-400">
                Email
                <input
                  value={formState.email}
                  onChange={handleFieldChange("email")}
                  className="mt-1 w-full rounded-2xl border border-white/10 bg-black/40 px-3 py-2 text-sm text-white focus:border-emerald-400 focus:outline-none"
                />
              </label>
              <label className="text-xs uppercase tracking-[0.3em] text-slate-400">
                Phone
                <input
                  value={formState.phone}
                  onChange={handleFieldChange("phone")}
                  className="mt-1 w-full rounded-2xl border border-white/10 bg-black/40 px-3 py-2 text-sm text-white focus:border-emerald-400 focus:outline-none"
                />
              </label>
              <label className="text-xs uppercase tracking-[0.3em] text-slate-400">
                Company / organisation
                <input
                  value={formState.company}
                  onChange={handleFieldChange("company")}
                  className="mt-1 w-full rounded-2xl border border-white/10 bg-black/40 px-3 py-2 text-sm text-white focus:border-emerald-400 focus:outline-none"
                />
              </label>
              <label className="text-xs uppercase tracking-[0.3em] text-slate-400">
                Role / Job title
                <input
                  value={formState.role}
                  onChange={handleFieldChange("role")}
                  className="mt-1 w-full rounded-2xl border border-white/10 bg-black/40 px-3 py-2 text-sm text-white focus:border-emerald-400 focus:outline-none"
                />
              </label>
            </div>
            <div className="space-y-2">
              <label className="text-xs uppercase tracking-[0.35em] text-slate-400">
                Contact type
                <select
                  value={formState.type}
                  onChange={handleFieldChange("type")}
                  className="mt-1 w-full rounded-2xl border border-white/10 bg-black/40 px-3 py-2 text-sm text-white focus:outline-none"
                >
                  {CONTACT_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-xs uppercase tracking-[0.3em] text-slate-400">
                Tags (comma separated)
                <input
                  value={formState.tags}
                  onChange={handleFieldChange("tags")}
                  className="mt-1 w-full rounded-2xl border border-white/10 bg-black/40 px-3 py-2 text-sm text-white focus:outline-none"
                />
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-slate-300">
                <input
                  type="checkbox"
                  checked={formState.marketingOptIn}
                  onChange={handleFieldChange("marketingOptIn")}
                  className="accent-emerald-400"
                />
                Can receive marketing emails
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-slate-300">
                <input
                  type="checkbox"
                  checked={formState.marketingContactAllowed}
                  onChange={handleFieldChange("marketingContactAllowed")}
                  className="accent-emerald-400"
                />
                Marketing contact allowed
              </label>
              <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400">
                <span className="rounded-full border border-white/20 px-3 py-1 text-[10px] uppercase tracking-[0.3em]">
                  {SOURCE_LABELS[contact.source] || "Source unknown"}
                </span>
                <span>Created {formatDateTime(contact.createdAt)}</span>
                <span>Updated {formatDateTime(contact.updatedAt)}</span>
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.35em] text-slate-400">
                Notes
                <textarea
                  value={formState.notes}
                  onChange={handleFieldChange("notes")}
                  rows={3}
                  className="mt-1 w-full rounded-2xl border border-white/10 bg-black/40 px-3 py-2 text-sm text-white focus:outline-none"
                />
              </label>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-[0.35em] text-slate-400">Associated listings</p>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    value={listingQuery}
                    onChange={(event) => setListingQuery(event.target.value)}
                    placeholder="Search listings"
                    className="rounded-2xl border border-white/10 bg-black/40 px-3 py-1.5 text-xs text-white focus:outline-none"
                  />
                  {listingLoading && <span className="text-xs text-slate-400">Searching…</span>}
                </div>
              </div>
              {listingError && <div className="text-xs text-rose-300">{listingError}</div>}
              <div className="space-y-2">
                {associatedListings.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between rounded-2xl border border-white/10 bg-black/40 px-3 py-2 text-xs text-slate-200"
                  >
                    <div>
                      <p className="font-semibold text-white">{item.refCode}</p>
                      <p className="text-xs text-slate-400">{item.status}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveListing(item)}
                      className="text-rose-400 hover:text-rose-200"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
              {listingResults.length > 0 && (
                <div className="space-y-1">
                  {listingResults.map((result) => (
                    <button
                      key={result.id}
                      type="button"
                      className="flex w-full items-center justify-between rounded-2xl border border-white/10 bg-white/5 px-3 py-2 text-xs text-white hover:bg-white/10 transition"
                      onClick={() => handleAddListing(result)}
                    >
                      <span>{result.refCode || result.address?.line1 || result.id}</span>
                      <span className="text-slate-400">{result.status || "Listing"}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="rounded-2xl border border-emerald-400/40 bg-emerald-500/80 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-500 transition disabled:opacity-60"
              >
                {saving ? "Saving…" : "Save changes"}
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="rounded-2xl border border-rose-500/60 px-4 py-2 text-sm font-semibold text-rose-300 hover:border-rose-400 transition disabled:opacity-60"
              >
                {deleting ? "Deleting…" : "Delete contact"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
